import React, { Component } from "react";
import PropTypes from "prop-types";
import { observable } from "mobx";
import { observer } from "mobx-react";
import { Table, Form, Button, Confirm, Pagination } from "semantic-ui-react";
import cx from "classnames";
import CreditAccount from "~/models/CreditAccount";
import ContentLoader from "~/components/element/ContentLoader";
import FormStore from "~/stores/FormStore";
import { getValidationErrors } from "~/utils/ReactReformed/ValidateSchema";
import CreditAccountForm, { validationSchema } from "./CreditAccountForm";

@observer
class CreditAccounts extends Component {
  @observable editFormStore = null;

  defaultState = {
    isLoading: false,
    currentPage: 1,
    creditAccountForDeletion: null,
  };

  constructor(props) {
    super(props);

    this.resetEditFormStore();
    this.doUpdate = this.doUpdate.bind(this);
    this.doDelete = this.doDelete.bind(this);
    this.resetEditFormStore = this.resetEditFormStore.bind(this);
    this.cancelDeletion = this.cancelDeletion.bind(this);

    this.state = this.defaultState;
  }

  onEdit(creditAccount) {
    this.editFormStore = new FormStore({
      // Only used for tracking the matching record
      creditAccountId: creditAccount.creditAccountId,
      amount: creditAccount.amount,
      description: creditAccount.description,
    });
  }

  showConfirmDelete(creditAccount) {
    this.setState({
      creditAccountForDeletion: creditAccount,
    });
  }

  doDelete() {
    const { onDelete } = this.props;
    const { creditAccountForDeletion } = this.state;

    const promise = onDelete(creditAccountForDeletion.creditAccountId);

    this.setState({ creditAccountForDeletion: null, isLoading: true });

    if (promise) {
      promise.finally(() => this.resetState());
    } else {
      this.resetState();
    }
  }

  doUpdate() {
    const { onUpdate } = this.props;
    const { model } = this.editFormStore;

    const result = getValidationErrors(validationSchema, model);

    if (!result.isValid) {
      return;
    }

    // sanity check.
    if (typeof model.creditAccountId === "undefined") {
      throw new Error("update credit account requires a credit account id");
    }

    const amount = parseInt(model.amount, 10);
    const description = model.description.trim();

    const promise = onUpdate(model.creditAccountId, { description, amount });

    if (promise) {
      this.setState({ isLoading: true });
      promise.finally(() => {
        this.setState({ isLoading: false });
      });
    }

    this.resetEditFormStore();
  }

  resetEditFormStore() {
    this.editFormStore = new FormStore({ amount: "", description: "" });
  }

  cancelDeletion() {
    this.setState({ creditAccountForDeletion: null });
  }

  resetState() {
    this.setState(this.defaultState);
    this.resetEditFormStore();
  }

  changePage(currentPage) {
    const { onPageChange } = this.props;

    this.setState({ currentPage });
    const promise = onPageChange(currentPage);

    if (typeof promise.then !== "undefined") {
      this.setState({ isLoading: true });
      promise.finally(() => {
        this.setState({ isLoading: false });
      });
    }
  }

  doCreate({ amount, description }) {
    const { onCreate } = this.props;
    const promise = onCreate({ amount, description });

    if (promise && typeof promise.then !== "undefined") {
      this.setState({ isLoading: true });
      promise.finally(() => {
        this.setState({ isLoading: false });
      });
    }

    // got to page 1 so user can see the new record at first place.
    this.setState({ currentPage: 1 });

    return promise;
  }

  render() {
    const { balance, creditAccounts, totalCreditAccounts, recordsPerPage } = this.props;
    const { isLoading, currentPage, creditAccountForDeletion } = this.state;
    const { model: editModel, blurred, onChange: editOnChange } = this.editFormStore;

    // For display, there should always be one page at least.
    const totalPages = Math.max(Math.ceil(totalCreditAccounts / recordsPerPage), 1);
    // If for any reason the current page is bigger than the total pages, then default to the last page.
    const actualActivePage = Math.min(currentPage, totalPages);

    const editErrors = getValidationErrors(validationSchema, editModel);

    return (
      <div className="credit-accounts">
        {creditAccountForDeletion && (
          <Confirm
            open
            content={`Confirm deletion for: "${creditAccountForDeletion.description}", amount: ${creditAccountForDeletion.amount}`}
            onCancel={this.cancelDeletion}
            onConfirm={this.doDelete}
          />
        )}
        <h3>Balance: {balance}</h3>
        <p>
          Showing {creditAccounts.length} items. Page {actualActivePage}/{totalPages}
        </p>
        <CreditAccountForm onSubmit={data => this.doCreate(data)} style={{ float: "right" }} />
        <Pagination
          activePage={actualActivePage}
          totalPages={totalPages}
          onPageChange={(_event, { activePage }) => this.changePage(activePage)}
        />
        <br />
        <br />
        <Form>
          <Table>
            <Table.Header>
              <Table.Row>
                <Table.HeaderCell>date</Table.HeaderCell>
                <Table.HeaderCell>credits</Table.HeaderCell>
                <Table.HeaderCell>description</Table.HeaderCell>
                <Table.HeaderCell />
                <Table.HeaderCell />
              </Table.Row>
            </Table.Header>
            <Table.Body>
              {creditAccounts.map(creditAccount => (
                <Table.Row key={creditAccount.creditAccountId}>
                  <Table.Cell>{creditAccount.creationDts.format("YYYY-MM-DD HH:mm:ss")}</Table.Cell>
                  <Table.Cell>
                    {editModel.creditAccountId === creditAccount.creditAccountId ? (
                      <Form.Input
                        value={editModel.amount}
                        name="amount"
                        onChange={editOnChange}
                        error={!editErrors.fields.amount.isValid && blurred.amount}
                      />
                    ) : (
                      <span className={cx({ credited: creditAccount.amount > 0 })}>{creditAccount.amount}</span>
                    )}
                  </Table.Cell>
                  <Table.Cell>
                    {editModel.creditAccountId === creditAccount.creditAccountId ? (
                      <Form.Input
                        fluid
                        value={editModel.description}
                        name="description"
                        onChange={editOnChange}
                        error={!editErrors.fields.description.isValid && blurred.description}
                      />
                    ) : (
                      creditAccount.description
                    )}
                  </Table.Cell>
                  <Table.Cell>
                    {editModel.creditAccountId === creditAccount.creditAccountId ? (
                      <Button disabled={!editErrors.isValid} size="small" primary onClick={this.doUpdate}>
                        Save
                      </Button>
                    ) : (
                      <Button size="small" primary onClick={() => this.onEdit(creditAccount)}>
                        Edit
                      </Button>
                    )}
                  </Table.Cell>
                  <Table.Cell>
                    {editModel.creditAccountId === creditAccount.creditAccountId ? (
                      <Button size="small" secondary onClick={this.resetEditFormStore}>
                        Cancel
                      </Button>
                    ) : (
                      <Button size="small" color="red" onClick={() => this.showConfirmDelete(creditAccount)}>
                        Delete
                      </Button>
                    )}
                  </Table.Cell>
                </Table.Row>
              ))}
            </Table.Body>
          </Table>
        </Form>
        <br />
        <br />
        <Pagination
          activePage={actualActivePage}
          totalPages={totalPages}
          onPageChange={(_event, { activePage }) => this.changePage(activePage)}
        />
        <ContentLoader isLoading={isLoading} />
      </div>
    );
  }
}

CreditAccounts.propTypes = {
  balance: PropTypes.number.isRequired,
  recordsPerPage: PropTypes.number.isRequired,
  totalCreditAccounts: PropTypes.number.isRequired,
  creditAccounts: PropTypes.arrayOf(PropTypes.instanceOf(CreditAccount)),
  onDelete: PropTypes.func.isRequired,
  onUpdate: PropTypes.func.isRequired,
  onCreate: PropTypes.func.isRequired,
  onPageChange: PropTypes.func.isRequired,
};

export default CreditAccounts;
